import React, {useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button, IconButton} from '@udemy/react-core-components';
import {BlockSkeleton} from '@udemy/react-reveal-components';

import {useAssessmentsCatalogQuery} from 'src/generated/graphql';
import {getRuntimeConfig} from 'src/pages/api/config.api';

import {AssessmentsBannerCard} from './assessments-banner-card.react-component';
import styles from './assessments-banner.module.less';
import {HowAssessmentsWorkModal} from './how-assessments-work-modal.react-component';
import {selectPersonalizedAssessments} from './select-personalized-assessments';

interface AssessmentsBannerSkeletonProps {
    className: string;
}

const AssessmentsBannerSkeleton: React.FC<AssessmentsBannerSkeletonProps> = ({className}) => (
    <div className={`${styles['banner-skeleton']} ${className}`} aria-hidden>
        <BlockSkeleton className={styles['skeleton-title']} />
        <BlockSkeleton className={styles['skeleton-subtitle']} />
        <div className={styles['cards-row']}>
            <BlockSkeleton className={styles['card-skeleton']} />
            <BlockSkeleton className={styles['card-skeleton']} />
            <BlockSkeleton className={styles['card-skeleton']} />
        </div>
        <BlockSkeleton className={styles['skeleton-cta']} />
    </div>
);

interface AssessmentsBannerProps {
    onDismiss: () => void;
    topicInterestIds: string[];
    hasCertInterests: boolean;
    hasSkillInterests: boolean;
    className?: string;
    forceShow?: boolean;
}

export const AssessmentsBanner: React.FC<AssessmentsBannerProps> = ({
    onDismiss,
    topicInterestIds,
    hasCertInterests,
    hasSkillInterests,
    className = '',
    forceShow = false,
}) => {
    const {gettext} = useI18n();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const assessmentsCatalog = useAssessmentsCatalogQuery();

    const all = (assessmentsCatalog.data?.assessments ?? []).filter((a) => a != null);
    const assessments = selectPersonalizedAssessments(
        all,
        topicInterestIds,
        hasCertInterests,
        hasSkillInterests,
    );

    if (
        !forceShow &&
        !assessmentsCatalog.isLoading &&
        (assessmentsCatalog.isError || assessments.length === 0)
    ) {
        return null;
    }

    const browseAssessmentsUrl = `${getRuntimeConfig('MONOLITH_URL')}/skills-assessment/`;
    const firstAssessmentUrl = assessments[0]?.urlLanding ?? browseAssessmentsUrl;

    if (assessmentsCatalog.isLoading) {
        return <AssessmentsBannerSkeleton className={className} />;
    }

    return (
        <div className={`${styles.banner} ${className}`} data-purpose="assessments-banner">
            <IconButton
                udStyle="ghost"
                size="small"
                onClick={onDismiss}
                aria-label={gettext('Dismiss assessments banner')}
                className={styles['dismiss-btn']}
            >
                <Icon glyph="close" label={false} />
            </IconButton>

            <div className={styles.header}>
                <div className={styles['header-content']}>
                    <h2 className="ud-heading-serif-xl">
                        {gettext('Ready to Level Up? Assess your skills and see where you stand')}
                    </h2>
                    <p className="ud-text-md">
                        {gettext(
                            'Based on your survey, take a 10-minute assessment to skip the basics and get your personalized learning path.',
                        )}
                    </p>
                </div>
                <Button
                    udStyle="ghost"
                    size="small"
                    onClick={() => setIsModalOpen(true)}
                    className={styles['how-it-works-btn']}
                >
                    <Icon glyph="info" label={false} size="small" />
                    {gettext('How assessments work')}
                </Button>
            </div>

            <div className={styles['cards-row']}>
                {assessments.map((assessment) => (
                    <AssessmentsBannerCard key={assessment.id} assessment={assessment} />
                ))}

                <div className={styles['testimonial-card']}>
                    <div className={styles['testimonial-header']}>
                        <div className={styles['quote-circle']}>
                            <Icon glyph="quote" label={false} size="small" />
                        </div>
                        <span className="ud-heading-sm">{gettext('Why Assessments Matter')}</span>
                    </div>
                    <blockquote className={`ud-text-sm ${styles['testimonial-quote']}`}>
                        {gettext(
                            '"Taking an assessment allowed me to skip the beginner modules I already knew and dive directly into intermediate training. It saved me hours of redundant work and made me feel confident I was starting at the right level to actually upskill."',
                        )}
                    </blockquote>
                    <p className={`ud-text-xs ${styles['testimonial-attribution']}`}>
                        {gettext('— Sarah J., Digital Marketing Learner')}
                    </p>
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    componentClass="a"
                    href={firstAssessmentUrl}
                    udStyle="primary"
                    size="medium"
                >
                    {gettext('Start Free Skills Assessment')}
                </Button>
                <a href={browseAssessmentsUrl} className={`ud-heading-sm ${styles['browse-link']}`}>
                    {gettext('Browse all assessments')}
                    <Icon glyph="arrow-right" label={false} size="small" />
                </a>
            </div>

            <HowAssessmentsWorkModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};
